import type { QuoteOriginEnum, QuoteStatusEnum, QuotesListParams } from '@gta/api-client'
import { z } from 'zod'

/**
 * 报价域 search params 契约(URL 是唯一状态源)。
 * 参数名与 `QuotesListParams` 同名直传;取值面对齐 `QuoteStatusEnum` / `QuoteOriginEnum`。
 */

export const QUOTE_STATUS_VALUES = [
  'draft',
  'sent',
  'accepted',
  'locked',
  'expired',
  'superseded',
] as const satisfies readonly QuoteStatusEnum[]

export const QUOTE_ORIGIN_VALUES = ['from_proposal', 'direct_manual'] as const satisfies readonly QuoteOriginEnum[]

/** 同订单域:正则之外再校一次日历合法性,`2026-13-99` 这种不能放到后端去 400。 */
function isRealCalendarDate(value: string): boolean {
  const [year, month, day] = value.split('-').map(Number)
  if (year === undefined || month === undefined || day === undefined) return false
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  )
}

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isRealCalendarDate)
  .optional()
  .catch(undefined)

export const DEFAULT_QUOTES_PAGE = 1

export const quotesSearchSchema = z.object({
  /** 页码(1 基);默认第一页,`page=1` 由 stripSearchParams 摘掉,不往 URL 里塞冗余键。 */
  page: z.coerce.number().int().min(1).default(DEFAULT_QUOTES_PAGE).catch(DEFAULT_QUOTES_PAGE),
  status: z.enum(QUOTE_STATUS_VALUES).optional().catch(undefined),
  quote_origin: z.enum(QUOTE_ORIGIN_VALUES).optional().catch(undefined),
  /** 关键词:报价资源号 / 客户名。 */
  search: z.string().trim().min(1).optional().catch(undefined),
  /** 创建日期区间(闭区间,按日期比较)。 */
  created_after: isoDate,
  created_before: isoDate,
})

export type QuotesSearch = z.infer<typeof quotesSearchSchema>

/** 等于默认值的参数不回写 URL(stripSearchParams;A4 全站统一)。 */
export const QUOTES_SEARCH_DEFAULTS = {
  page: DEFAULT_QUOTES_PAGE,
} satisfies Partial<QuotesSearch>

export const QUOTES_PAGE_SIZE = 20

function blankToUndefined(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : undefined
}

/**
 * 把一次筛选变更合并进现有 search(除非显式带 `page`,任何筛选变更都回到第一页)。
 * 「等于默认值的键不进 URL」不再手写丢键 —— 交给路由上的 stripSearchParams 统一处理(A4)。
 */
export function applyQuotesSearchPatch(prev: QuotesSearch, patch: Partial<QuotesSearch>): QuotesSearch {
  const merged: QuotesSearch = { ...prev, ...patch }
  return {
    ...merged,
    page: 'page' in patch ? merged.page : DEFAULT_QUOTES_PAGE,
    search: blankToUndefined(merged.search),
  }
}

export function toQuotesListParams(search: QuotesSearch): QuotesListParams {
  return {
    page: search.page,
    page_size: QUOTES_PAGE_SIZE,
    ...(search.status ? { status: search.status } : {}),
    ...(search.quote_origin ? { quote_origin: search.quote_origin } : {}),
    ...(search.search ? { search: search.search } : {}),
    ...(search.created_after ? { created_after: search.created_after } : {}),
    ...(search.created_before ? { created_before: search.created_before } : {}),
  }
}

export function hasQuotesFilter(search: QuotesSearch): boolean {
  return Boolean(
    search.status ?? search.quote_origin ?? search.search ?? search.created_after ?? search.created_before,
  )
}
